import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import PropertyCard from "@/components/PropertyCard";
import LeadForm from "@/components/LeadForm";
import { Skeleton } from "@/components/ui/skeleton";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { MapPin, Train, Briefcase, School, Hospital, TrendingUp, CheckCircle2 } from "lucide-react";

export default function LocationPage() {
  const { seoSlug } = useParams();
  const slug = (seoSlug || "").startsWith("properties-in-") ? seoSlug.replace("properties-in-", "") : null;
  const [loc, setLoc] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoc(null); setNotFound(false); window.scrollTo(0, 0);
    if (!slug) { setNotFound(true); return; }
    api.get(`/locations/${slug}`).then((r) => setLoc(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="max-w-3xl mx-auto px-4 py-32 text-center" data-testid="not-found-page"><h1 className="font-display text-3xl text-navy">Page Not Found</h1><Link to="/properties" className="text-gold underline mt-4 inline-block">Browse Properties</Link></div>;
  if (!loc) return <div className="max-w-7xl mx-auto px-4 py-10 space-y-6"><Skeleton className="h-72 rounded-2xl" /><Skeleton className="h-40" /></div>;

  const faqJsonLd = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: (loc.faqs || []).map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };

  return (
    <div>
      <SEO title={`Properties in ${loc.name}, Bangalore | Prices, Projects & Investment | Mathura Homes`}
        description={loc.overview?.slice(0, 160)} image={loc.hero_image} jsonLd={faqJsonLd} />

      <section className="relative h-[42vh] min-h-[320px] flex items-end">
        <img src={loc.hero_image} alt={loc.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-navy/70" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 w-full">
          <p className="text-sm text-muted-foreground text-gray-300"><Link to="/" className="hover:text-gold">Home</Link> / <span className="text-gold">{loc.name}</span></p>
          <h1 className="font-display text-4xl md:text-5xl text-white mt-2">Properties in {loc.name}</h1>
          <p className="text-gray-200 mt-3 max-w-2xl">{loc.short} · Bangalore</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          <Block title="Area Overview"><p className="text-muted-foreground leading-relaxed">{loc.overview}</p></Block>
          <Block title="Real Estate Market Summary"><p className="text-muted-foreground leading-relaxed">{loc.market_summary}</p>
            <div className="mt-4 p-4 rounded-xl bg-gold/10 text-navy inline-flex items-center gap-2"><TrendingUp className="w-5 h-5 text-gold-dark" /> <span className="font-semibold">Typical Price Range:</span> {loc.price_range}</div>
          </Block>
          <Block title="Connectivity"><div className="flex items-start gap-3"><Train className="w-5 h-5 text-gold mt-0.5" /><p className="text-muted-foreground leading-relaxed">{loc.connectivity}</p></div></Block>

          <div className="grid sm:grid-cols-3 gap-4">
            <ListCard icon={Briefcase} title="Employment Hubs" items={loc.employment_hubs} />
            <ListCard icon={School} title="Schools" items={loc.schools} />
            <ListCard icon={Hospital} title="Hospitals" items={loc.hospitals} />
          </div>

          <Block title="Investment Advantages">
            <div className="grid sm:grid-cols-2 gap-3">
              {(loc.investment || []).map((a) => <div key={a} className="flex items-center gap-2 text-navy text-sm"><CheckCircle2 className="w-5 h-5 text-emerald-brand shrink-0" /> {a}</div>)}
            </div>
          </Block>

          {loc.projects?.length > 0 && (
            <Block title={`Available Projects in ${loc.name}`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">{loc.projects.map((p) => <PropertyCard key={p.id} p={p} />)}</div>
            </Block>
          )}

          {loc.faqs?.length > 0 && (
            <Block title="Frequently Asked Questions">
              <Accordion type="single" collapsible className="w-full">
                {loc.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`}>
                    <AccordionTrigger className="text-left text-navy font-semibold">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Block>
          )}
        </div>

        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-24 bg-white rounded-2xl border border-gray-100 shadow-card p-6">
            <h3 className="font-display text-xl text-navy mb-1">Enquire About {loc.name}</h3>
            <p className="text-sm text-muted-foreground mb-4">Get expert guidance on properties in this area.</p>
            <LeadForm variant="quick" leadType="location_enquiry" projectName={loc.name} sourcePage={`location:${loc.slug}`} redirectThankYou={false} />
          </div>
        </aside>
      </div>
    </div>
  );
}

const Block = ({ title, children }) => <div><h2 className="font-display text-2xl md:text-3xl text-navy mb-4">{title}</h2>{children}</div>;
const ListCard = ({ icon: Icon, title, items }) => (
  <div className="p-4 rounded-xl border border-gray-100 bg-muted">
    <p className="flex items-center gap-2 font-semibold text-navy mb-2"><Icon className="w-4 h-4 text-gold" /> {title}</p>
    <ul className="text-sm text-muted-foreground space-y-1">{(items || []).map((it) => <li key={it}>• {it}</li>)}</ul>
  </div>
);
